import React, { useState, useEffect } from 'react'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useAuthStore } from '../../stores/authStore'
import { usePermissions } from '../../hooks/usePermissions'
import PageHelp from '../../components/PageHelp'
import FieldHelp from '../../components/FieldHelp'

interface JournalEntry {
  id: string
  entryNumber: string
  entryDate: string
  description: string
  reference?: string
  totalDebit: string
  totalCredit: string
  status: string
  lines: JournalEntryLine[]
  postedAt?: string
  postedByUser?: {
    firstName: string
    lastName: string
  }
}

interface JournalEntryLine {
  id: string
  accountId: string
  debit: string
  credit: string
  description?: string
  account: {
    code: string
    name: string
  }
}

const JournalEntries: React.FC = () => {
  const { user } = useAuthStore()
  const { hasPermission } = usePermissions(user)
  const [entries, setEntries] = useState<JournalEntry[]>([])
  const [accounts, setAccounts] = useState<any[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showPostModal, setShowPostModal] = useState(false)
  const [showViewModal, setShowViewModal] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null)
  const [isLoadingEntry, setIsLoadingEntry] = useState(false)
  const [formData, setFormData] = useState({
    entryDate: new Date().toISOString().split('T')[0],
    reference: '',
    description: '',
    fiscalYear: new Date().getFullYear().toString(),
    lines: [
      { accountId: '', debit: '', credit: '', description: '' },
      { accountId: '', debit: '', credit: '', description: '' },
    ] as Array<{ accountId: string; debit: string; credit: string; description: string }>,
  })

  useEffect(() => {
    fetchEntries()
    fetchAccounts()
  }, [])

  const fetchEntries = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get('/api/erp/journal-entries')
      setEntries(response.data.data || [])
    } catch (error: any) {
      console.error('Error fetching journal entries:', error)
      toast.error('Failed to load journal entries')
    } finally {
      setIsLoading(false)
    }
  }

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/erp/accounts', { params: { isActive: 'true' } })
      setAccounts(response.data.data || [])
    } catch (error) {
      console.error('Error fetching accounts:', error)
    }
  }

  const addLine = () => {
    setFormData({
      ...formData,
      lines: [...formData.lines, { accountId: '', debit: '', credit: '', description: '' }],
    })
  }

  const removeLine = (index: number) => {
    if (formData.lines.length > 2) {
      setFormData({
        ...formData,
        lines: formData.lines.filter((_, i) => i !== index),
      })
    }
  }

  const updateLine = (index: number, field: string, value: string) => {
    const newLines = [...formData.lines]
    newLines[index] = { ...newLines[index], [field]: value }
    if (field === 'debit' && value) {
      newLines[index].credit = ''
    }
    if (field === 'credit' && value) {
      newLines[index].debit = ''
    }
    setFormData({ ...formData, lines: newLines })
  }

  const calculateTotals = () => {
    const totalDebit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.debit) || 0), 0)
    const totalCredit = formData.lines.reduce((sum, line) => sum + (parseFloat(line.credit) || 0), 0)
    return { totalDebit, totalCredit, difference: totalDebit - totalCredit }
  }

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    const totals = calculateTotals()

    if (Math.abs(totals.difference) > 0.01) {
      toast.error(`Entry is not balanced. Difference: ${totals.difference.toFixed(2)}`)
      return
    }

    try {
      const payload = {
        entryDate: formData.entryDate,
        reference: formData.reference || undefined,
        description: formData.description,
        fiscalYear: formData.fiscalYear,
        lines: formData.lines.map((line) => ({
          accountId: line.accountId,
          debit: parseFloat(line.debit) || 0,
          credit: parseFloat(line.credit) || 0,
          description: line.description || undefined,
        })),
      }

      await axios.post('/api/erp/journal-entries', payload)
      toast.success('Journal entry created successfully')
      setShowCreateModal(false)
      setFormData({
        entryDate: new Date().toISOString().split('T')[0],
        reference: '',
        description: '',
        fiscalYear: new Date().getFullYear().toString(),
        lines: [
          { accountId: '', debit: '', credit: '', description: '' },
          { accountId: '', debit: '', credit: '', description: '' },
        ],
      })
      fetchEntries()
    } catch (error: any) {
      console.error('Error creating journal entry:', error)
      toast.error(error.response?.data?.error || 'Failed to create journal entry')
    }
  }

  const handlePost = async (entryId: string) => {
    try {
      await axios.post(`/api/erp/journal-entries/${entryId}/post`)
      toast.success('Journal entry posted successfully')
      setShowPostModal(false)
      setSelectedEntry(null)
      fetchEntries()
    } catch (error: any) {
      console.error('Error posting journal entry:', error)
      toast.error(error.response?.data?.error || 'Failed to post journal entry')
    }
  }

  if (!hasPermission('erp', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You don't have permission to view journal entries.</p>
        </div>
      </div>
    )
  }

  const totals = calculateTotals()

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-start">
        <div>
          <div className="flex items-center">
            <h1 className="text-2xl font-bold text-gray-900">Journal Entries</h1>
            <PageHelp pageIdentifier="journal-entries" title="Journal Entries" />
          </div>
          <p className="text-gray-600 mt-1">Manual accounting entries</p>
        </div>
        {hasPermission('erp', 'write') && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Create Entry</span>
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading journal entries...</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50 cursor-pointer" onClick={async () => {
                  setIsLoadingEntry(true)
                  try {
                    const response = await axios.get(`/api/erp/journal-entries/${entry.id}`)
                    setSelectedEntry(response.data.data)
                    setShowViewModal(true)
                  } catch (error: any) {
                    console.error('Error fetching journal entry:', error)
                    toast.error('Failed to load journal entry details')
                  } finally {
                    setIsLoadingEntry(false)
                  }
                }}>
                  <td className="px-4 py-2">{entry.entryNumber}</td>
                  <td className="px-4 py-2">{new Date(entry.entryDate).toLocaleDateString()}</td>
                  <td className="px-4 py-2">{entry.description}</td>
                  <td className="px-4 py-2 text-right">{parseFloat(entry.totalDebit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2 text-right">{parseFloat(entry.totalCredit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</td>
                  <td className="px-4 py-2">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      entry.status === 'POSTED' ? 'bg-green-100 text-green-800' :
                      entry.status === 'DRAFT' ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {entry.status}
                    </span>
                  </td>
                  <td className="px-4 py-2" onClick={(e) => e.stopPropagation()}>
                    <div className="flex space-x-2">
                      <button
                        onClick={async () => {
                          setIsLoadingEntry(true)
                          try {
                            const response = await axios.get(`/api/erp/journal-entries/${entry.id}`)
                            setSelectedEntry(response.data.data)
                            setShowViewModal(true)
                          } catch (error: any) {
                            console.error('Error fetching journal entry:', error)
                            toast.error('Failed to load journal entry details')
                          } finally {
                            setIsLoadingEntry(false)
                          }
                        }}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        disabled={isLoadingEntry}
                      >
                        View
                      </button>
                      {entry.status === 'DRAFT' && hasPermission('erp_journal_entry', 'post') && (
                        <button
                          onClick={() => {
                            setSelectedEntry(entry)
                            setShowPostModal(true)
                          }}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Post
                        </button>
                      )}
                      {entry.status === 'DRAFT' && hasPermission('erp_journal_entry', 'write') && (
                        <button
                          onClick={() => {
                            // TODO: Implement edit functionality
                            toast('Edit functionality coming soon', { icon: 'ℹ️' })
                          }}
                          className="text-green-600 hover:text-green-800 text-sm font-medium"
                        >
                          Edit
                        </button>
                      )}
                      {entry.status === 'DRAFT' && hasPermission('erp_journal_entry', 'delete') && (
                        <button
                          onClick={async () => {
                            if (!confirm('Are you sure you want to delete this journal entry? This action cannot be undone.')) {
                              return
                            }
                            try {
                              await axios.delete(`/api/erp/journal-entries/${entry.id}`)
                              toast.success('Journal entry deleted successfully')
                              fetchEntries()
                            } catch (error: any) {
                              console.error('Error deleting journal entry:', error)
                              toast.error(error.response?.data?.error || 'Failed to delete journal entry')
                            }
                          }}
                          className="text-red-600 hover:text-red-800 text-sm font-medium"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {entries.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No journal entries found. Create your first entry to get started.
            </div>
          )}
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">Create Journal Entry</h2>
            <form onSubmit={handleCreate}>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Entry Date *
                      <FieldHelp content="The date when the transaction occurred. This is used as the posting date for GL Entries and determines which accounting period the entry belongs to." />
                    </label>
                    <input
                      type="date"
                      required
                      value={formData.entryDate}
                      onChange={(e) => setFormData({ ...formData, entryDate: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reference</label>
                    <input
                      type="text"
                      value={formData.reference}
                      onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                      className="w-full border border-gray-300 rounded px-3 py-2"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                  <input
                    type="text"
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Entry Lines *
                    <span className="ml-2 text-xs text-gray-500 font-normal">
                      (Minimum 2 lines required for double-entry bookkeeping)
                    </span>
                  </label>
                  <div className="mb-2 text-xs text-gray-600 bg-blue-50 p-2 rounded">
                    <strong>Tip:</strong> Enter <strong>either</strong> a Debit <strong>or</strong> a Credit for each line (not both). 
                    Total debits must equal total credits.
                  </div>
                  <div className="space-y-2">
                    {formData.lines.map((line, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-4">
                          <select
                            required
                            value={line.accountId}
                            onChange={(e) => updateLine(index, 'accountId', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                          >
                            <option value="">Select Account</option>
                            {accounts.map((acc) => (
                              <option key={acc.id} value={acc.id}>
                                {acc.code} - {acc.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.debit}
                            onChange={(e) => updateLine(index, 'debit', e.target.value)}
                            placeholder="Debit"
                            className="w-full border border-gray-300 rounded px-3 py-2"
                            disabled={!!line.credit}
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.credit}
                            onChange={(e) => updateLine(index, 'credit', e.target.value)}
                            placeholder="Credit"
                            className="w-full border border-gray-300 rounded px-3 py-2"
                            disabled={!!line.debit}
                          />
                        </div>
                        <div className="col-span-3">
                          <input
                            type="text"
                            value={line.description || ''}
                            onChange={(e) => updateLine(index, 'description', e.target.value)}
                            placeholder="Description (optional)"
                            className="w-full border border-gray-300 rounded px-2 py-2 text-sm"
                          />
                        </div>
                        <div className="col-span-1 flex items-center justify-center">
                          {formData.lines.length > 2 ? (
                            <button
                              type="button"
                              onClick={() => removeLine(index)}
                              className="text-red-600 hover:text-red-800 text-xl font-bold"
                              title="Remove this line"
                            >
                              ×
                            </button>
                          ) : (
                            <span className="text-gray-400 text-xs" title="Minimum 2 lines required">
                              —
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={addLine}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                    >
                      + Add Line
                    </button>
                  </div>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <div className="flex justify-between mb-1">
                    <span className="font-medium">Total Debit:</span>
                    <span>{totals.totalDebit.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span className="font-medium">Total Credit:</span>
                    <span>{totals.totalCredit.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</span>
                  </div>
                  <div className={`flex justify-between font-bold ${Math.abs(totals.difference) < 0.01 ? 'text-green-600' : 'text-red-600'}`}>
                    <span>Difference:</span>
                    <span>{totals.difference.toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</span>
                  </div>
                  {Math.abs(totals.difference) < 0.01 && totals.totalDebit > 0 && (
                    <div className="mt-2 text-xs text-green-600">
                      ✓ Entry is balanced and ready to create
                    </div>
                  )}
                  {Math.abs(totals.difference) >= 0.01 && (
                    <div className="mt-2 text-xs text-red-600">
                      ⚠ Entry must be balanced (debits = credits) before creating
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={Math.abs(totals.difference) >= 0.01}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  Create Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Post Modal */}
      {showPostModal && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Post Journal Entry</h2>
            <p className="mb-4">Are you sure you want to post entry {selectedEntry.entryNumber}? This will create GL Entries and cannot be undone.</p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowPostModal(false)
                  setSelectedEntry(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handlePost(selectedEntry.id)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Post Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Journal Entry Modal */}
      {showViewModal && selectedEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Journal Entry {selectedEntry.entryNumber}</h2>
              <button
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedEntry(null)
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              {/* Entry Details */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Entry Date</label>
                  <p className="mt-1 text-sm text-gray-900">{new Date(selectedEntry.entryDate).toLocaleDateString()}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Status</label>
                  <p className="mt-1">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      selectedEntry.status === 'POSTED' ? 'bg-green-100 text-green-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {selectedEntry.status}
                    </span>
                  </p>
                </div>
                {selectedEntry.postedAt && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Posted At</label>
                    <p className="mt-1 text-sm text-gray-900">{new Date(selectedEntry.postedAt).toLocaleString()}</p>
                  </div>
                )}
              </div>

              {selectedEntry.reference && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">Reference</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedEntry.reference}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">Description</label>
                <p className="mt-1 text-sm text-gray-900">{selectedEntry.description}</p>
              </div>

              {/* Entry Lines */}
              {selectedEntry.lines && selectedEntry.lines.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Entry Lines</label>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Debit</th>
                          <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Credit</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {selectedEntry.lines.map((line, index) => (
                          <tr key={line.id || index}>
                            <td className="px-4 py-2 text-sm">
                              {line.account ? `${line.account.code} - ${line.account.name}` : '-'}
                            </td>
                            <td className="px-4 py-2 text-right text-sm">
                              {parseFloat(line.debit || '0').toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                            </td>
                            <td className="px-4 py-2 text-right text-sm">
                              {parseFloat(line.credit || '0').toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                            </td>
                            <td className="px-4 py-2 text-sm text-gray-600">{line.description || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td className="px-4 py-2 font-bold text-sm">Total</td>
                          <td className="px-4 py-2 text-right font-bold text-sm">
                            {parseFloat(selectedEntry.totalDebit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                          </td>
                          <td className="px-4 py-2 text-right font-bold text-sm">
                            {parseFloat(selectedEntry.totalCredit).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Posted Info */}
              {selectedEntry.postedAt && selectedEntry.postedByUser && (
                <div className="text-sm text-gray-500">
                  Posted on {new Date(selectedEntry.postedAt).toLocaleString()} by {selectedEntry.postedByUser.firstName} {selectedEntry.postedByUser.lastName}
                </div>
              )}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => {
                  setShowViewModal(false)
                  setSelectedEntry(null)
                }}
                className="px-4 py-2 border border-gray-300 rounded text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default JournalEntries
